/* eslint-disable @typescript-eslint/explicit-function-return-type */
import React, { useEffect, useRef, useState } from 'react'
import {
  getAmiDataOptions
} from '../../device-details/components/dataSourceComponents/ChartOptions'
import * as HighCharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import { useMediaQuery } from 'react-responsive'
import { Card, CardContent, CircularProgress, Grid } from '@mui/material'
import AuthError from '../../../components/ErrorComponents/AuthError'
import { type thirtyDayState } from '../redux/thirtyDaySlice'
import dayjs, { type Dayjs } from 'dayjs'
import LanguageStrings from '../../../i18n/locales'

interface GetGraphReturn {
  type: 'error' | 'data'
  element: JSX.Element
}

const DataQuality = (props: { thirtyDay: thirtyDayState, fethThirtyDayData: any, tenant: any }): JSX.Element => {
  const ThirtyDayStrings = LanguageStrings().AMIDataCollectionStrings.thirtyDay

  const containerRef = useRef<any>(null)
  const hrRef = useRef<any>(null)
  const titleRef = useRef<any>(null)
  const graphRef = useRef<any>(null)

  const isSmallScreen = useMediaQuery({ maxWidth: 2000 })
  const isLargeScreen = useMediaQuery({ minWidth: 2001 })
  const oneMonthAgo = new Date()
  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1)
  const ChartData: any[][] = []

  const defaultDate = {
    end: dayjs(new Date()),
    start: dayjs(new Date((new Date()).setMonth(new Date().getMonth() - 1)))
  }
  const [start] = useState<Dayjs | null | undefined>(defaultDate.start)
  const [end] = useState<Dayjs | null | undefined>(defaultDate.end)

  const fetchThirtydata = (): any => {
    if (start !== (undefined) && start !== null && end !== undefined && end !== null && start <= end) {
      const startMoment = start
        .clone()
        .set('hour', 0)
        .set('minute', 0)
        .set('second', 0)
      const endMoment = end
        .clone()
        .set('hour', 23)
        .set('minute', 59)
        .set('second', 59)
      props.fethThirtyDayData(props.tenant, startMoment.format('YYYY-MM-DDTHH:mm:ssZ'), endMoment.format('YYYY-MM-DDTHH:mm:ssZ'))
    }
  }

  useEffect(() => {
    fetchThirtydata()
  }, [start, end])

  const data: any[] = []
  if (props.thirtyDay.thirtyDayData != null) {
    data.push(props.thirtyDay.thirtyDayData)

    if (data[0].timestamps !== undefined) {
      const timestamps = []
      for (const entry of data[0].timestamps) {
        const x = new Date(entry).getTime()
        timestamps.push(x)
      }
      const values = data[0].kpiValues
      let temp = []
      for (let i = 0; i < values.length; i++) {
        const formattedDate = formatDate(timestamps[i])
        temp.push(formattedDate)
        temp.push(values[i])
        ChartData.push(temp)
        temp = []
      }
    }
  }

  function formatDate (inputDate: any) {
    const date = new Date(inputDate)
    const year = date.getUTCFullYear()
    const month = String(date.getUTCMonth() + 1).padStart(2, '0')
    const day = String(date.getUTCDate()).padStart(2, '0')
    const hours = String(date.getUTCHours()).padStart(2, '0')
    const minutes = String(date.getUTCMinutes()).padStart(2, '0')
    const formattedDate = `${year}-${month}-${day} ${hours}:${minutes}`
    return formattedDate
  }

  let cardHeight = '100%' // Default height
  let fSize = '15px'
  let lineHght = '20px'

  if (isSmallScreen) {
    cardHeight = '45vh' // Height for small screens
    fSize = '16px'
    lineHght = '20px'
  }

  if (isLargeScreen) {
    cardHeight = '32vh' // Height for large screens
    fSize = '18px'
    lineHght = '26px'
  }
  function getGraph (): GetGraphReturn {
    if (props.thirtyDay.isLoading) {
      return (
        {
          type: 'error',
          element: (<div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}><CircularProgress /></div>)
        }
      )
    } else if (props.thirtyDay.httpStatus === 401) {
      return (
        {
          type: 'error',
          element: (<div className='authErrorThirtyDaysTend daystrend401'><AuthError errorMessage="Unauthorized"/></div>)
        }
      )
    } else if (props.thirtyDay.httpStatus === 403) {
      return (
        {
          type: 'error',
          element: (<div className='authErrorThirtyDaysTend daystrend401'><AuthError errorMessage="accessForbidden"/></div>)
        }
      )
    } else if ((props.thirtyDay.thirtyDayData === null && props.thirtyDay.httpStatus === 200)) {
      return (
        {
          type: 'error',
          element: (<div className='authErrorThirtyDaysTend daystrend401'><AuthError errorMessage="NoDataPresent"/></div>)
        }
      )
    } else if (
      props.thirtyDay.thirtyDayData !== undefined &&
      props.thirtyDay.thirtyDayData !== null
    ) {
      return (
        {
          type: 'data',
          element: (<HighchartsReact
            highcharts={HighCharts}
            options={getAmiDataOptions('#A78D34', ChartData, containerRef, hrRef, titleRef)}
          />)
        }
      )
    } else {
      return (
        {
          type: 'error',
          element: (<div className='authErrorThirtyDaysTend daystrend401'><AuthError errorMessage="cannotFetch" retry={fetchThirtydata}/></div>)
        }
      )
    }
  }
  return (
    <div style={{ height: cardHeight }}>
      <Card sx={{ height: '100%', borderRadius: '10px', backgroundColor: '#313030', color: '#f0f0f0' }}>
        <div style={{ fontSize: '18px', padding: '15px' }}>{ThirtyDayStrings.Title}</div>
        <CardContent sx={{ height: '100%' }}>
          <Grid sx={{ height: '100%' }}>
            <Grid sx={{ height: '100%' }}>
              {
                (getGraph().type === 'error')
                  ? getGraph().element
                  : <Grid>
                  <div ref={containerRef} style={{ display: 'flex', alignItems: 'center', height: '100%', fontSize: fSize, lineHeight: lineHght }}>
                    <hr ref={hrRef} style={{ height: '22px', width: '5px', marginRight: '20px', border: 'none', backgroundColor: '#A78D34' }}/>
                    <div ref={titleRef}>{ThirtyDayStrings.SRR}</div>
                    <div ref={graphRef} className="centerChart" style={{ height: '100%', width: '80%' }}>
                      {getGraph().element}
                    </div>
                  </div>
                </Grid>
              }
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </div>
  )
}

export default DataQuality
